import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { AuthFlowService, UiAction } from '@app/core/auth/auth-flow.service';
import { BaseViewModel, BaseViewModelState, LoadingStatus } from '../../shared/view-model/base-view-model';

export interface LogInViewModelState extends BaseViewModelState {
  isAuthenticated: boolean;
  loginUserName: string;
  disableLoginButton: boolean;
  isCallingApi: boolean;
  action: UiAction;
}

const initialState: LogInViewModelState = {
  isAuthenticated: false,
  loginUserName: '',
  disableLoginButton: true,
  isCallingApi: false,
  action: 'idle',
  status: 'idle',
  errorMessage: null,
};

@Injectable()
export class LogInViewModel extends BaseViewModel<LogInViewModelState> {
  constructor(
    private readonly storeAuth: StoreAuthService,
    private readonly authFlow: AuthFlowService,
    private readonly destroyRef: DestroyRef,
  ) {
    super(initialState);

    this.storeAuth
      .getIsLoggedInOservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoggedIn) => this.patchState({ isAuthenticated: isLoggedIn }));

    this.storeAuth
      .getLoginUserNameObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => this.patchState({ loginUserName: name || '' }));

    this.authFlow.loginUiState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.patchState({
          disableLoginButton: state.disableLoginButton,
          isCallingApi: state.isCallingApi,
          action: state.action,
          status: this.mapActionToStatus(state.action, state.isCallingApi),
        });
      });
  }

  init(): void {
    this.authFlow.init();
  }

  handlePrimaryAction(forceNavigation = true): void {
    this.authFlow.handlePrimaryAction(forceNavigation);
  }

  loginWithDifferentAccount(): void {
    this.authFlow.loginWithDifferentAccount();
  }

  private mapActionToStatus(action: UiAction, isCallingApi: boolean): LoadingStatus {
    if (isCallingApi) {
      return 'loading';
    }

    switch (action) {
      case 'idle':
        return 'idle';
      case 'post-login':
        return 'success';
      default:
        return 'loading';
    }
  }

}
